import { Link, useParams } from "react-router-dom";
import AccountMenu from "components/account-menu";
import { TOUR_TARGETS } from "components/tour";
import { ComponentProps, useContext, useEffect, useState } from "react";
import { CiqIconBox } from "components/svg-icons";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import ProjectLogoIcon from "components/svg-icons/project-logo";
import AppWhiteLogoIcon from "components/svg-icons/app-white-logo-icon";
import RightArrowIcon from "components/svg-icons/right-arrow";
import {
  QUERY_GET_ENTITY_COUNT_V2,
  QUERY_GET_PRECON_ENTITY_COUNT
} from "services/graphQL/ciq-queries";
import { ProjectPermissionEnum } from "constants/index";
import {
  getProjectDashboardPath,
  getProjectHomePath,
  isPreconstructionProjectHome
} from "utils/project-home-path";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import { DownOutlined } from "@ant-design/icons";
import { Popover } from "antd";
import { DateTimePanel } from "./navbar";

import styles from "./navbar.module.scss";

type CardFeature = ComponentProps<typeof CiqIconBox>["feature"];

type PhaseMenuItem = {
  to: string;
  feature: CardFeature;
  title: string;
  description?: string;
  testId: string;
};

// Tile badge at the left of every dashboard navbar card: 36×36
// (size + 2 × padding).
function CardTile({ feature }: { feature: CardFeature }) {
  return (
    <div className="flex justify-center items-center w-10 shrink-0 h-full pl-1">
      <CiqIconBox feature={feature} size={20} padding={8} />
    </div>
  );
}

// The inactive phase collapses into one card whose dropdown lists its modules.
function PhaseMenuCard({
  feature,
  title,
  items,
  showBeta = false
}: {
  feature: CardFeature;
  title: string;
  items: PhaseMenuItem[];
  showBeta?: boolean;
}) {
  const content = (
    <div className="flex flex-col bg-ciq-header-100 rounded-lg shadow-xl bg-opacity-100 space-y-1">
      {items.map((item, index) => (
        <Link
          key={item.to}
          to={item.to}
          className={`flex items-center px-5 py-3 space-x-2 cursor-pointer hover:bg-black ${
            index === 0 ? "rounded-t-lg" : ""
          } ${index === items.length - 1 ? "rounded-b-lg" : ""}`}
          data-testid={item.testId}
        >
          {/* Dropdown tiles are 30×30 (size + 2 × padding). */}
          <CiqIconBox feature={item.feature} size={14} padding={8} />
          <div className="flex flex-col text-white">
            <div className="font-medium">{item.title}</div>
            {item.description && (
              <div className="text-xs text-white text-opacity-60">
                {item.description}
              </div>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
  return (
    <Popover
      content={content}
      classNames={{ root: styles.popoverNavbarNoPadding }}
      placement="bottomRight"
      // antd v6 dropped Tooltip/Popover's `showArrow` in favour of `arrow`.
      // `showArrow={false}` became a no-op, so the arrow came back at its
      // default `colorBgElevated` (white) against this dark custom content —
      // the stray white triangle above the panel.
      arrow={false}
    >
      <div className="group relative cursor-pointer flex items-center w-44 h-12 rounded border border-solid border-ciq-primary-300 bg-ciq-primary-400 text-white hover:text-white">
        {showBeta && (
          <div
            className="absolute right-0 top-0 px-2 py-0.5 rounded-bl rounded-tr border-solid border-0 border-b border-l border-ciq-primary-900 bg-ciq-primary-900 bg-opacity-60"
            style={{ fontSize: 10 }}
          >
            Beta
          </div>
        )}
        <div className="absolute right-1.5 bottom-0">
          <DownOutlined className="text-white group-hover:text-ciq-golden-purple-500 text-opacity-20 group-hover:text-opacity-100" />
        </div>

        <CardTile feature={feature} />

        <div className="flex flex-col p-1 pl-2">
          <div className="font-medium">{title}</div>
        </div>
      </div>
    </Popover>
  );
}

function PreconCard() {
  const { projectId } = useParams<{ projectId: string }>();
  return (
    <PhaseMenuCard
      feature="PRECON"
      title="Precon"
      showBeta
      items={[
        {
          to: `/project/${projectId}/design-packages`,
          feature: "DESIGN_PACKAGE",
          title: "Design Packages",
          description: "Coordinate with Design Packages",
          testId: "projectdashboardnavbar-link-coordinate-design-packages"
        },
        {
          to: `/project/${projectId}/design-scope`,
          feature: "DESIGN_SCOPE",
          title: "Design Scope",
          description: "Classify scope, LOD and bid coverage",
          testId: "projectdashboardnavbar-link-design-scope"
        },
        {
          to: `/project/${projectId}/bid-packages`,
          feature: "BID_PACKAGE",
          title: "Bid Packages",
          description: "Coordinate Buyouts",
          testId: "projectdashboardnavbar-link-coordinate-buyouts"
        }
      ]}
    />
  );
}

function ConstructionCard() {
  const { projectId } = useParams<{ projectId: string }>();
  return (
    <PhaseMenuCard
      feature="CONSTRUCTION"
      title="Construction"
      items={[
        {
          to: getProjectDashboardPath(projectId),
          feature: "PROJECT_DASHBOARD",
          title: "Project Dashboard",
          testId: "projectdashboardnavbar-link-construction-project-dashboard"
        },
        {
          to: `/project/${projectId}/submittals`,
          feature: "SUBMITTALS",
          title: "Submittals",
          testId: "projectdashboardnavbar-link-construction-submittals"
        },
        {
          to: `/project/${projectId}/materials`,
          feature: "MATERIALS",
          title: "Materials",
          testId: "projectdashboardnavbar-link-construction-materials"
        },
        {
          to: `/project/${projectId}/schedule/project-schedules`,
          feature: "SCHEDULES",
          title: "Schedules",
          testId: "projectdashboardnavbar-link-construction-schedules"
        }
      ]}
    />
  );
}

function LogCard({
  color,
  title,
  count,
  to,
  feature,
  tourId
}: {
  color: string;
  title: string;
  count: number | null;
  to: string;
  feature: CardFeature;
  tourId?: string;
}) {
  return (
    <Link
      to={to}
      className="relative flex items-center w-44 h-12 shrink-0 rounded border border-solid border-ciq-primary-300 bg-ciq-primary-400 text-white hover:text-white"
      data-testid="projectdashboardnavbar-link-logcard"
      data-tour={tourId}
    >
      <div className="absolute right-1 bottom-0">
        <RightArrowIcon />
      </div>
      <CardTile feature={feature} />
      <div className="flex flex-col p-1 pl-2 pr-1 whitespace-nowrap">
        <div className="font-medium">{title}</div>
        <div className="font-bold text-xs" style={{ color }}>
          {count}
        </div>
      </div>
    </Link>
  );
}

function SmallCard({
  name,
  to,
  tourId
}: {
  name: string;
  to: string;
  tourId?: string;
}) {
  return (
    <Link
      to={to}
      className="flex justify-between border border-solid border-ciq-primary-300 bg-ciq-primary-400 rounded px-2 py-0.5 text-white hover:text-white text-xs font-medium"
      data-testid="projectdashboardnavbar-link-smallcard"
      data-tour={tourId}
    >
      <div className="pl-1">{name}</div>
      <RightArrowIcon />
    </Link>
  );
}

function ProjectDashboardNavBar() {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    projectDetails,
    gqlClientForProject,
    tokenContents,
    isFeatureFlagEnabled
  } = useContext(ProjectContext);

  const [entityCount, setEntityCount] = useState({
    submittals: null,
    materials: null,
    schedules: null
  });

  const isBuyoutFeatureEnabled = isFeatureFlagEnabled("BUYOUT");
  const cannotViewDesignPage = isPermissionNotGrantted(
    ProjectPermissionEnum.ViewDesignPage,
    tokenContents?.role ?? ""
  );
  const isPreconstructionStage = isPreconstructionProjectHome(
    projectDetails,
    isBuyoutFeatureEnabled,
    !cannotViewDesignPage
  );
  const projectHomePath = getProjectHomePath(projectId, isPreconstructionStage);

  const cannotViewProjectReports = isPermissionNotGrantted(
    ProjectPermissionEnum.ViewProjectReports,
    tokenContents?.role ?? ""
  );

  const { data: entityCountData } = useCIQQuery(QUERY_GET_ENTITY_COUNT_V2, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  useEffect(() => {
    if (entityCountData) {
      const submittals =
        entityCountData?.submittals_aggregate?.aggregate?.count;
      const materials = entityCountData?.material_aggregate?.aggregate?.count;
      const schedules = entityCountData.schedule_import_details?.length;
      setEntityCount({ submittals, materials, schedules });
    }
  }, [entityCountData]);

  const { data: preconCountData } = useCIQQuery(QUERY_GET_PRECON_ENTITY_COUNT, {
    client: gqlClientForProject,
    skip: !gqlClientForProject || !isPreconstructionStage
  });
  const preconCount = {
    designPackages:
      preconCountData?.design_package_aggregate?.aggregate?.count ?? null,
    designScope:
      preconCountData?.design_scope_aggregate?.aggregate?.count ?? null,
    bidPackages:
      preconCountData?.bid_package_aggregate?.aggregate?.count ?? null
  };

  return (
    <div className="flex flex-col w-full bg-ciq-primary-200 text-white hover:text-white p-4">
      <div className="flex justify-between">
        <Link
          to="/projects"
          className="flex"
          data-testid="projectdashboardnavbar-link-app-logo"
        >
          <div className="w-[120px] flex">
            <AppWhiteLogoIcon />
          </div>
        </Link>
        <div className="flex space-x-4 items-center cursor-pointer">
          <AccountMenu />
        </div>
      </div>
      <div className="flex px-7 py-2 justify-between">
        <Link
          to={projectHomePath}
          className="flex space-x-2 text-white hover:text-white"
          data-testid="projectdashboardnavbar-link-project-logo"
        >
          <ProjectLogoIcon />
          {projectDetails?.id && (
            <div className="flex flex-col">
              <div className="text-xl font-semibold truncate max-w-md">
                <TruncatedTextWithTooltip text={projectDetails?.name} />
              </div>
              <DateTimePanel />
            </div>
          )}
        </Link>
        <div className="flex items-center space-x-4">
          {isPreconstructionStage ? (
            <>
              <LogCard
                color="var(--ciq-golden-purple-600)"
                title="Design Packages"
                count={preconCount.designPackages}
                to={`/project/${projectId}/design-packages`}
                feature="DESIGN_PACKAGE"
              />
              <LogCard
                color="var(--ciq-blue-600)"
                title="Design Scope"
                count={preconCount.designScope}
                to={`/project/${projectId}/design-scope`}
                feature="DESIGN_SCOPE"
              />
              <LogCard
                color="var(--ciq-orange-400)"
                title="Bid Packages"
                count={preconCount.bidPackages}
                to={`/project/${projectId}/bid-packages`}
                feature="BID_PACKAGE"
              />
              <ConstructionCard />
            </>
          ) : (
            <>
              {isBuyoutFeatureEnabled && !cannotViewDesignPage && (
                <PreconCard />
              )}
              <LogCard
                color="var(--ciq-primary-900-light)"
                title="Submittals"
                count={entityCount.submittals}
                to={`/project/${projectId}/submittals`}
                feature="SUBMITTALS"
                tourId={TOUR_TARGETS.NAV_SUBMITTALS}
              />
              <LogCard
                color="var(--ciq-teal-200)"
                title="Materials"
                count={entityCount.materials}
                to={`/project/${projectId}/materials`}
                feature="MATERIALS"
                tourId={TOUR_TARGETS.NAV_MATERIALS}
              />
              <LogCard
                color="var(--ciq-orange-400)"
                title="Schedules"
                count={entityCount.schedules}
                to={`/project/${projectId}/schedule/project-schedules`}
                feature="SCHEDULES"
                tourId={TOUR_TARGETS.NAV_SCHEDULES}
              />
            </>
          )}
          <div className="flex flex-col-reverse justify-between w-28 h-12">
            <SmallCard
              name="Settings"
              to={`/project/${projectId}/settings`}
              tourId={TOUR_TARGETS.NAV_SETTINGS}
            />
            {!cannotViewProjectReports && (
              <SmallCard
                name="Reports"
                to={`/project/${projectId}/reports`}
                tourId={TOUR_TARGETS.NAV_REPORTS}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectDashboardNavBar;
